import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Save, Search, RefreshCw, Paperclip, X, FileText, Image, File } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { toast } from 'sonner';
import api from '../lib/api';

// Helper to get file icon based on type
const getFileIcon = (file) => {
  if (file.type?.startsWith('image/')) return Image;
  if (file.type?.includes('pdf')) return FileText;
  return File;
};

// Format file size
const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const NewTicketPage = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [settings, setSettings] = useState(null);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [saving, setSaving] = useState(false);
  const [userSearchOpen, setUserSearchOpen] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [formData, setFormData] = useState({
    summary: '',
    description: '',
    assigned_to: '',
    category: 'General',
    priority: 'Medium',
    created_by_email: '',
    created_by_name: '',
    organization: 'Gamer Packaging, Inc.',
  });

  useEffect(() => {
    fetchSettings();
    fetchUsers();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await api.getTicketSettings();
      setSettings(res.data);
      if (res.data.default_assignee) {
        setFormData(prev => ({ ...prev, assigned_to: res.data.default_assignee }));
      }
      if (res.data.default_organization) {
        setFormData(prev => ({ ...prev, organization: res.data.default_organization }));
      }
    } catch (err) {
      console.error('Failed to fetch settings:', err);
    }
  };

  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      const res = await api.getAzureUsers();
      setUsers(res.data.users || []);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleUserSelect = (user) => {
    setFormData(prev => ({
      ...prev,
      created_by_name: user.displayName || user.name,
      created_by_email: user.mail || user.email,
    }));
    setUserSearchOpen(false);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(f => f.size <= 10 * 1024 * 1024); // 10MB limit
    if (validFiles.length < files.length) {
      toast.error('Some files exceed the 10MB limit and were not added');
    }
    setAttachments(prev => [...prev, ...validFiles]);
    e.target.value = ''; // Reset input
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!formData.summary.trim()) {
      toast.error('Please enter a summary');
      return;
    }
    
    setSaving(true);
    try {
      // First create the ticket
      const res = await api.createManagedTicket(formData);
      const ticketId = res.data.ticket_id;
      
      // Then upload attachments if any
      if (attachments.length > 0) {
        await api.uploadTicketAttachments(ticketId, attachments);
        toast.success(`Ticket created with ${attachments.length} attachment(s)`);
      } else {
        toast.success('Ticket created');
      }
      
      navigate(`/tickets/${ticketId}`);
    } catch (err) {
      console.error('Failed to create ticket:', err);
      toast.error('Failed to create ticket');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="sticky top-0 z-40 bg-white border-b border-slate-200 shadow-sm">
        <div className="flex items-center justify-between px-6 py-3">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/tickets/managed')} className="gap-2">
              <ChevronLeft className="h-4 w-4" />
              Back
            </Button>
            <h1 className="font-bold text-slate-800">New Ticket</h1>
          </div>
          <Button onClick={handleSubmit} disabled={saving} className="bg-blue-600 hover:bg-blue-700 gap-2">
            <Save className="h-4 w-4" />
            {saving ? 'Creating...' : 'Create Ticket'}
          </Button>
        </div>
      </header>

      <div className="p-6">
        <div className="max-w-3xl mx-auto">
          <Card>
            <CardHeader>
              <CardTitle>Create New Ticket</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Summary *</label>
                <Input
                  value={formData.summary}
                  onChange={(e) => handleChange('summary', e.target.value)}
                  placeholder="Brief description of the issue"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  placeholder="Detailed description of the issue..."
                  className="min-h-[150px]"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-gray-700">Requester</label>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={fetchUsers}
                    disabled={loadingUsers}
                    className="h-6 text-xs"
                  >
                    <RefreshCw className={`h-3 w-3 mr-1 ${loadingUsers ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                </div>
                <Popover open={userSearchOpen} onOpenChange={setUserSearchOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      className="w-full justify-start text-left font-normal"
                    >
                      <Search className="h-4 w-4 mr-2 text-gray-400" />
                      {formData.created_by_name 
                        ? `${formData.created_by_name} (${formData.created_by_email})`
                        : "Search for a user..."
                      }
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[400px] p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Search users..." />
                      <CommandList>
                        <CommandEmpty>No users found.</CommandEmpty>
                        <CommandGroup>
                          {users.map((user) => (
                            <CommandItem
                              key={user.id || user.mail}
                              value={`${user.displayName || user.name} ${user.mail || user.email}`}
                              onSelect={() => handleUserSelect(user)}
                              className="cursor-pointer"
                            >
                              <div className="flex flex-col">
                                <span className="font-medium">{user.displayName || user.name}</span>
                                <span className="text-xs text-gray-500">{user.mail || user.email}</span>
                                {user.department && (
                                  <span className="text-xs text-gray-400">{user.department}</span>
                                )}
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Assign To</label>
                  <Select value={formData.assigned_to || 'unassigned'} onValueChange={(v) => handleChange('assigned_to', v === 'unassigned' ? '' : v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select assignee" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unassigned">Unassigned</SelectItem>
                      {settings?.it_staff?.filter(s => s && s.trim()).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <Select value={formData.category} onValueChange={(v) => handleChange('category', v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {settings?.categories?.filter(c => c && c.trim()).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                  <Select value={formData.priority} onValueChange={(v) => handleChange('priority', v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {settings?.priorities?.filter(p => p && p.trim()).map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Organization</label>
                <Input
                  value={formData.organization}
                  onChange={(e) => handleChange('organization', e.target.value)}
                  placeholder="Gamer Packaging, Inc."
                  disabled
                  className="bg-gray-50"
                />
              </div>

              {/* Attachments */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">Attachments</label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    className="gap-1"
                  >
                    <Paperclip className="h-4 w-4" />
                    Add Files
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    onChange={handleFileSelect}
                    className="hidden"
                    accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.zip"
                  />
                </div>
                
                {attachments.length > 0 ? (
                  <div className="border rounded-lg p-3 space-y-2 bg-gray-50">
                    {attachments.map((file, idx) => {
                      const FileIcon = getFileIcon(file);
                      return (
                        <div key={idx} className="flex items-center justify-between bg-white p-2 rounded border">
                          <div className="flex items-center gap-2">
                            <FileIcon className="h-4 w-4 text-gray-400" />
                            <span className="text-sm truncate max-w-xs">{file.name}</span>
                            <span className="text-xs text-gray-400">({formatFileSize(file.size)})</span>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeAttachment(idx)}
                            className="h-6 w-6 p-0 text-gray-400 hover:text-red-500"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400 text-center py-4 border rounded-lg border-dashed">
                    No attachments. Click "Add Files" to attach documents or images.
                  </p>
                )}
                <p className="text-xs text-gray-400 mt-1">Max 10MB per file. Supported: images, PDF, Office documents, text files.</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default NewTicketPage;
